import { useContext } from "react"
import { Dropdown, DropdownChangeParams } from "primereact/dropdown"
import { classNames } from "primereact/utils"
import userContext from "../../context/user";

interface TimeOption {
  value: string;
}

export const TimeRange = ({ value, onChange, disableOptionValidation, error }: TimeRangeProps) => {
  const { user } = useContext(userContext)

  const generateTimeOptions = (): TimeOption[] => {
    const workingTimeFrom: string = user?.workingTime?.from || "08:00"
    const workingTimeTo: string = user?.workingTime?.to || "16:00"
    const hourFrom: number = parseInt(workingTimeFrom, 10)
    const hourTo: number = parseInt(workingTimeTo, 10)
    const res: TimeOption[] = []

    // add values and remove leading zeros for double digit hours
    for (let i = hourFrom; i <= hourTo; i++) {
      res.push({ value: `0${i}:00`.slice(-5) }) // whole hour
      res.push({ value: `0${i}:30`.slice(-5) }) // half hour
    }

    // if from time starts with half hour, remove the first whole hour
    if (workingTimeFrom.indexOf(":30") > -1) {
      res.shift()
    }

    // if to time ends with whole hour, remove the last half hour
    if (workingTimeTo.indexOf(":30") === -1) {
      res.pop()
    }

    return res
  }

  const timeOptions = generateTimeOptions()
  const splits: string[] = value.split("-")
  const from: string = splits[0]
  const to: string = splits[1]

  const isFromOptionDisabled = (option: TimeOption): boolean => {
    if (disableOptionValidation) {
      return false
    }

    const toIndex = timeOptions.findIndex((timeOption) => timeOption.value === to)
    const optionIndex = timeOptions.findIndex((timeOption) => timeOption.value === option.value)

    return optionIndex >= toIndex
  }

  const isToOptionDisabled = (option: TimeOption): boolean => {
    if (disableOptionValidation) {
      return false
    }

    const fromIndex = timeOptions.findIndex((timeOption) => timeOption.value === from)
    const optionIndex = timeOptions.findIndex((timeOption) => timeOption.value === option.value)

    return optionIndex <= fromIndex
  }

  const handleFromChange = (event: DropdownChangeParams): void => {
    onChange(`${event.target.value}-${to}`)
  }

  const handleToChange = (event: DropdownChangeParams): void => {
    onChange(`${from}-${event.target.value}`)
  }

  return (
    <>
      <Dropdown options={timeOptions}
        optionLabel={"value"} optionValue={"value"} value={from}
        onChange={handleFromChange} optionDisabled={isFromOptionDisabled}
        className={classNames({ "inline-flex w-6": true, "p-invalid": error })} />
      <Dropdown options={timeOptions}
        optionLabel={"value"} optionValue={"value"} value={to}
        onChange={handleToChange} optionDisabled={isToOptionDisabled}
        className={classNames({ "inline-flex w-6": true, "p-invalid": error })} />
    </>
  )
}

export interface TimeRangeProps {
  value: string;
  onChange: (value: string) => void;
  disableOptionValidation?: boolean;
  error?: boolean;
}
